import { Capacitor } from "@capacitor/core";
import { isPlatform, getPlatforms } from "@ionic/vue";

export function humanizedData(date: Date): string {
  const year = date.getFullYear();
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthName = monthNames[date.getMonth()];
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${year}-${monthName}-${day} ${hours}:${minutes}:${seconds}`;
}

export function normalizedDate(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-indexed, so we add 1
  const day = date.getDate().toString().padStart(2, '0');
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

// utils.js
export async function checkUrl(url: string): Promise<boolean>{
  try {
    const response = await fetch(url, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error(`Error checking URL: ${url}`, error);
    return false;
  }
}

// Convert file to base64 data URL (avoids CSP blob: restriction for image cropper)
export const fileToDataUrl = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.readAsDataURL(file)
  })
}

export type Platform = 'ios' | 'android' | 'electron' | 'mobile-web' | 'desktop-web' | 'web';
const VALID_PLATFORMS: Platform[] = ['ios', 'android', 'electron', 'mobile-web', 'desktop-web', 'web'];
let webPlatform: Platform | undefined; // cached per page load: rotation changes screen.width, but core services are set up once

export function getPlatform(): Platform {
  // DEV-only override via query param or localStorage
  if (import.meta.env.DEV) {
    const override = new URLSearchParams(window.location.search).get('force_platform') || localStorage.getItem('force_platform');
    if (override && VALID_PLATFORMS.includes(override as Platform)) return override as Platform;
    return 'web';
  }
  // Native platforms
  const nativePlatform = Capacitor.getPlatform();
  if (nativePlatform === 'ios' || nativePlatform === 'android') return nativePlatform;
  if (getPlatforms().includes('electron')) return 'electron';
  // Web platforms
  if (!Capacitor.isNativePlatform()) return webPlatform ??= screen.width <= 768 ? 'mobile-web' : 'desktop-web';
  return 'web';
}

// Check if device operates in remote mode (sapi instead of SignalR sync)
export function isRemoteDevice(): boolean {
  const platform = getPlatform();
  if (platform !== 'desktop-web' && platform !== 'mobile-web') return false;
  const flag = localStorage.getItem('remote_mode');
  return flag !== null ? flag === 'true' : platform === 'mobile-web'; // mobile-web: default on, desktop-web: default off
}
